import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheck, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PublicFooter from "@/components/PublicFooter";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

export default function Signup() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    nome_agenzia: "", partita_iva: "", indirizzo: "", codice_sdi: "", pec: "",
    nome: "", cognome: "", email: "", username: "", password: "",
    plan_id: "base",
    privacy_accepted: false, terms_accepted: false,
  });
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans || [])).catch(() => setPlans([]));
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.privacy_accepted || !form.terms_accepted) {
      setError("Devi accettare l'informativa privacy e le condizioni di utilizzo per continuare");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/signup", form);
      localStorage.setItem("access_token", res.data.access_token);
      await refresh();
      nav("/admin?welcome=1", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Torna alla homepage
        </Link>
        <Card className="p-8 border-slate-200 card-elevated">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5 text-slate-900" />
            <span className="font-heading font-bold text-slate-900">Incassi Giornalieri</span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-3">Crea la tua agenzia</h1>
          <p className="text-sm text-slate-600 mt-1">7 giorni gratis · nessuna carta richiesta · cancellazione in qualsiasi momento</p>

          <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="signup-form">
            <section>
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Dati agenzia</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Nome agenzia</Label>
                  <Input data-testid="signup-agenzia" required value={form.nome_agenzia} onChange={(e) => set("nome_agenzia", e.target.value)} placeholder="Es. Rossi Assicurazioni" />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <Input data-testid="signup-piva" required value={form.partita_iva} onChange={(e) => set("partita_iva", e.target.value)} placeholder="IT01234567890" />
                </div>
                <div>
                  <Label>Codice univoco SDI</Label>
                  <Input data-testid="signup-sdi" required value={form.codice_sdi} onChange={(e) => set("codice_sdi", e.target.value)} placeholder="ABCDEFG (7 caratteri)" />
                </div>
                <div className="md:col-span-2">
                  <Label>Indirizzo sede</Label>
                  <Input data-testid="signup-indirizzo" required value={form.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} placeholder="Via Roma 1, 20100 Milano (MI)" />
                </div>
                <div className="md:col-span-2">
                  <Label>PEC (opzionale)</Label>
                  <Input type="email" data-testid="signup-pec" value={form.pec} onChange={(e) => set("pec", e.target.value)} placeholder="agenzia@pec.it" />
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Dati agente</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nome</Label>
                  <Input data-testid="signup-nome" required value={form.nome} onChange={(e) => set("nome", e.target.value)} />
                </div>
                <div>
                  <Label>Cognome</Label>
                  <Input data-testid="signup-cognome" required value={form.cognome} onChange={(e) => set("cognome", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Email</Label>
                  <Input type="email" data-testid="signup-email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="agente@agenzia.it" />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input data-testid="signup-username" required value={form.username} onChange={(e) => set("username", e.target.value)} placeholder="es. lucarossi" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input type="password" data-testid="signup-password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="minimo 8 caratteri" />
                </div>
              </div>
            </section>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="signup-error">{error}</div>
            )}

            <section>
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Scegli il piano</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="signup-plans">
                {plans.map((p) => {
                  const selected = form.plan_id === p.id;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => set("plan_id", p.id)}
                      className={`text-left border rounded-lg p-4 transition-all ${selected ? "border-slate-900 bg-slate-900 text-white shadow-sm" : "border-slate-200 bg-white hover:border-slate-400"}`}
                      data-testid={`signup-plan-${p.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-heading font-bold text-lg">{p.name}</div>
                        {selected && <Check className="w-4 h-4" />}
                      </div>
                      <div className={`text-xs mt-0.5 ${selected ? "text-slate-300" : "text-slate-500"}`}>{p.tagline}</div>
                      <div className="mt-3">
                        <div className="font-heading font-bold text-xl font-mono-num">{formatEUR(p.price_net)}<span className={`text-xs font-normal ml-1 ${selected ? "text-slate-300" : "text-slate-500"}`}>/ mese</span></div>
                        <div className={`text-[11px] mt-0.5 ${selected ? "text-slate-300" : "text-slate-500"}`}>compresi oneri di legge</div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/10 text-xs space-y-1">
                        <div className={selected ? "text-white" : "text-slate-700"}>
                          {p.max_extra_users === null
                            ? "Collaboratori illimitati"
                            : p.max_extra_users === 0
                              ? "Solo utente singolo"
                              : `Fino a ${p.max_extra_users} collaboratori`}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-700">
              <div className="font-heading font-semibold">Prova gratuita di 7 giorni</div>
              <div className="text-xs text-slate-500 mt-1">Nessun addebito durante la prova: inserirai la carta solo al termine. Potrai cambiare piano in qualsiasi momento da Impostazioni → Abbonamento.</div>
            </div>

            {/* Consensi obbligatori */}
            <div className="space-y-3 pt-2" data-testid="signup-consents">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.privacy_accepted}
                  onChange={(e) => set("privacy_accepted", e.target.checked)}
                  required
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900"
                  data-testid="signup-privacy-checkbox"
                />
                <span className="text-sm text-slate-700">
                  Dichiaro di aver preso visione dell&apos;
                  <Link to="/privacy" target="_blank" className="text-blue-600 underline hover:text-blue-800" data-testid="signup-privacy-link">
                    informativa privacy
                  </Link>
                  {" "}e di prestare il consenso al trattamento dei miei dati personali.
                  <span className="text-red-600 ml-1">*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.terms_accepted}
                  onChange={(e) => set("terms_accepted", e.target.checked)}
                  required
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900"
                  data-testid="signup-terms-checkbox"
                />
                <span className="text-sm text-slate-700">
                  Dichiaro di aver letto e accettato le{" "}
                  <Link to="/termini" target="_blank" className="text-blue-600 underline hover:text-blue-800" data-testid="signup-terms-link">
                    condizioni generali di utilizzo
                  </Link>
                  {" "}del Servizio.
                  <span className="text-red-600 ml-1">*</span>
                </span>
              </label>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={submitting || !form.privacy_accepted || !form.terms_accepted}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="signup-submit"
            >
              {submitting ? "Creazione in corso…" : <>Inizia la prova gratuita <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Hai già un account? <Link to="/login" className="text-slate-900 underline">Accedi</Link>
            </p>
          </form>
        </Card>
      </div>
    </div>
    <PublicFooter />
    </>
  );
}
